import React, { useState, useEffect } from 'react';
import { getDealRoomBrandedName } from '../../utils/locale';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import { 
  BuildingOfficeIcon, 
  DocumentTextIcon, 
  EyeIcon, 
  LockClosedIcon, 
  PresentationChartBarIcon,
  CheckBadgeIcon,
  HandRaisedIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

// GraphQL Query for Succession Deals Widget Data
const GET_SUCCESSION_DEALS_WIDGET_DATA = gql`
  query GetSuccessionDealsWidgetData {
    getCurrentUser {
      id
      motivation
      inventory_user {
        id
        can_create_meeting_room
        can_match
        deals {
          id
          deal_name
          headline
          deal_state
          closed
          creation_date
          matches_count
          number_of_invites
          number_of_proposals
          number_of_ndas
          number_of_negotiations
        }
        deal_interests {
          id
          workflow_state
          inventory_user {
            id
          }
          deal {
            id
            deal_name
            headline
          }
        }
      }
    }
    company {
      id
      deal_room_branded_name
      deal_room_branded_name_fr
    }
  }
`;

const DealRoom = () => {
  const [selectedDealIndex, setSelectedDealIndex] = useState(0);

  return (
    <Query query={GET_SUCCESSION_DEALS_WIDGET_DATA}>
      {({ loading, error, data, refetch }) => {
        if (loading) {
          return (
            <div className="bg-white shadow rounded-lg p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="flex items-center">
                    <div className="rounded-full p-3 bg-gray-200 h-12 w-12"></div>
                    <div className="ml-4">
                      <div className="h-5 bg-gray-200 rounded w-20 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="space-y-4">
                {[...Array(5)].map((_, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-32 h-4 bg-gray-200 rounded"></div>
                    <div className="flex-grow mx-4 h-4 bg-gray-200 rounded"></div>
                    <div className="w-6 h-4 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if (error) {
          return (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-xl font-medium text-gray-900 mb-4">{dealRoomBrandedName}</h3>
              <div className="text-center">
                <p className="text-sm text-red-600 mb-4">Error loading deals data</p>
                <button
                  onClick={refetch}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Try Again
                </button>
              </div>
            </div>
          );
        }

         const currentUser = data?.getCurrentUser;
         const currentCompany = data?.company;
         const dealRoomBrandedName = getDealRoomBrandedName(currentCompany);
         const inventoryUser = currentUser?.inventory_user;
         const deals = inventoryUser?.deals?.filter(deal => deal.deal_state === 'published') || [];
         const dealInterests = inventoryUser?.deal_interests || [];
         
         // Match the exact logic from SuccessionDealsHome
         const userMotivation = currentUser?.motivation || '';
         const isSeller = userMotivation.includes('seller') || userMotivation.includes('succession');
         const canCreateDealRoom = inventoryUser?.can_create_meeting_room;
         const canMatch = inventoryUser?.can_match;
         const hasDealRoom = deals.length > 0;
         
         // Filter for active deal interests that belong to the current user (buyer)
         const activeDealInterests = dealInterests.filter(di => {
           // Security check: only show deal interests where the current user is the buyer
           const isCurrentUserBuyer = di.inventory_user?.id === inventoryUser?.id;
           const isActiveState = !['matching', 'declined', 'declined_by_buyer', 'archived'].includes(di.workflow_state);
           const hasValidDeal = di.deal && di.deal.id; // Ensure deal exists and has an ID
           
           return isCurrentUserBuyer && isActiveState && hasValidDeal;
         });
         const hasActiveInterests = activeDealInterests.length > 0;
         
         // Widget visibility logic: show if user has deals OR has active interests OR (can create deal room per SuccessionDealsHome logic)
         const shouldShowWidget = hasDealRoom || hasActiveInterests || (isSeller && canCreateDealRoom && canMatch && !hasDealRoom);
         
         if (!shouldShowWidget) {
           return null;
         }

         // No deals but can create - show create button (using exact SuccessionDealsHome logic)
         if (isSeller && canCreateDealRoom && canMatch && !hasDealRoom) {
           return (
             <div className="bg-white shadow rounded-lg p-6">
               <h3 className="text-xl font-medium text-gray-900 mb-4">{dealRoomBrandedName}</h3>
               <div className="text-center">
                 <HandRaisedIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                 <p className="text-gray-500 mb-4">Start your succession journey</p>
                 <a
                   href="/succession/deals/wizard"
                   className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                 >
                   <PlusIcon className="h-4 w-4 mr-2" />
                   Create Deal
                 </a>
               </div>
             </div>
           );
         }

         // Has deals - show the original deal room interface
         if (hasDealRoom) {
           // Get the currently selected deal
           const selectedDeal = deals[selectedDealIndex] || deals[0];
           
           // Real data from the selected deal
           const dealInterests = {
             matching_count: selectedDeal.matches_count || 0,
             invited_count: selectedDeal.number_of_invites || 0,
             reviewing_proposal_count: selectedDeal.number_of_proposals || 0,
             receiving_signature_count: selectedDeal.number_of_ndas || 0,
             awaiting_disclosing_signature_count: 0, // NDAs are already combined in number_of_ndas
             out_for_negotiating_count: selectedDeal.number_of_negotiations || 0
           };

           // Calculate metrics for the selected deal
           const dealStats = [
             {
               icon: CheckBadgeIcon,
               value: capitalizeFirstLetter(selectedDeal.deal_state),
               label: 'Status',
               color: 'text-green-500',
               bgColor: 'bg-green-100'
             },
             {
               icon: EyeIcon,
               value: dealInterests.invited_count.toString(),
               label: 'Interested Parties',
               color: 'text-blue-500',
               bgColor: 'bg-blue-100'
             },
             {
               icon: LockClosedIcon,
               value: (dealInterests.receiving_signature_count + dealInterests.awaiting_disclosing_signature_count).toString(),
               label: 'NDAs Signed',
               color: 'text-blue-500', 
               bgColor: 'bg-blue-100'
             },
             {
               icon: PresentationChartBarIcon,
               value: dealInterests.out_for_negotiating_count.toString(),
               label: 'Negotiations',
               color: 'text-blue-500',
               bgColor: 'bg-blue-100'
             }
           ];

           // Calculate the highest count for percentage calculation
           const maxCount = Math.max(
             dealInterests.matching_count,
             dealInterests.invited_count,
             dealInterests.reviewing_proposal_count,
             dealInterests.receiving_signature_count + dealInterests.awaiting_disclosing_signature_count,
             dealInterests.out_for_negotiating_count
           );

           // Deal flow funnel data
           const dealFlowStages = [
             { 
               label: 'Matching', 
               count: dealInterests.matching_count, 
               color: 'bg-blue-500',
               fillPercentage: maxCount > 0 ? (dealInterests.matching_count / maxCount) * 100 : 0
             },
             { 
               label: 'Invited', 
               count: dealInterests.invited_count, 
               color: 'bg-blue-500',
               fillPercentage: maxCount > 0 ? (dealInterests.invited_count / maxCount) * 100 : 0
             },
             { 
               label: 'Proposals', 
               count: dealInterests.reviewing_proposal_count, 
               color: 'bg-purple-500',
               fillPercentage: maxCount > 0 ? (dealInterests.reviewing_proposal_count / maxCount) * 100 : 0
             },
             { 
               label: 'NDAs', 
               count: dealInterests.receiving_signature_count + dealInterests.awaiting_disclosing_signature_count, 
               color: 'bg-orange-500',
               fillPercentage: maxCount > 0 ? ((dealInterests.receiving_signature_count + dealInterests.awaiting_disclosing_signature_count) / maxCount) * 100 : 0
             },
             { 
               label: 'Negotiating', 
               count: dealInterests.out_for_negotiating_count, 
               color: 'bg-green-500',
               fillPercentage: maxCount > 0 ? (dealInterests.out_for_negotiating_count / maxCount) * 100 : 0
             }
           ];

           return (
             <div className="bg-white shadow rounded-lg p-6">
               <div className="flex items-center justify-between mb-6">
                 <h3 className="text-xl font-medium text-gray-900">{dealRoomBrandedName}</h3>
                 <div className="flex items-center space-x-4">
                   {deals.length > 1 && (
                     <div className="relative">
                       <select
                         className="block appearance-none bg-white border border-gray-300 hover:border-gray-400 px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:ring focus:border-blue-300"
                         value={selectedDealIndex}
                         onChange={(e) => setSelectedDealIndex(parseInt(e.target.value))}
                       >
                         {deals.map((deal, index) => (
                           <option key={deal.id} value={index}>
                             {deal.headline || `Deal #${deal.deal_name}`}
                           </option>
                         ))}
                       </select>
                       <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                         <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                           <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                         </svg>
                       </div>
                     </div>
                   )}
                   <a
                     href={`/succession/deals/${selectedDeal.deal_name}`}
                     className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                   >
                     Manage Deal
                   </a>
                 </div>
               </div>
               
               {/* Deal Statistics and Funnel Side-by-Side */}
               <div className="flex flex-col md:flex-row gap-6 mb-2">
                 {/* Metrics (left) */}
                 <div className="md:w-5/12">
                   <div className="grid grid-cols-2 gap-6">
                     {dealStats.map((stat, index) => (
                       <div key={index} className="flex items-center">
                         <div className={`flex-shrink-0 rounded-full p-3 ${stat.bgColor}`}>
                           <stat.icon className={`h-6 w-6 ${stat.color}`} aria-hidden="true" />
                         </div>
                         <div className="ml-4">
                           <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                           <p className="text-sm text-gray-500">{stat.label}</p>
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>

                 {/* Funnel (right) */}
                 <div className="md:w-7/12">
                   <h4 className="text-lg font-medium text-gray-700 mb-2 text-center">Deal Flow Funnel</h4>
                   <FunnelChart stages={dealFlowStages} />
                 </div>
               </div>
             </div>
           );
         }

         // Has active deal interests (buyer view) - show deal opportunities
         if (hasActiveInterests) {
           return (
             <div className="bg-gradient-to-br from-white to-blue-50 shadow-lg rounded-xl p-6 border border-blue-100">
               <div className="flex items-center justify-between mb-6">
                 <div className="flex items-center space-x-3">
                   <div className="flex-shrink-0">
                     <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                       <EyeIcon className="w-6 h-6 text-white" />
                     </div>
                   </div>
                   <div>
                     <h3 className="text-xl font-semibold text-gray-900">{dealRoomBrandedName} Opportunities</h3>
                     <p className="text-sm text-gray-600">Active succession opportunities for you</p>
                   </div>
                 </div>
                 <div className="flex-shrink-0">
                   <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                     {activeDealInterests.length} Active
                   </span>
                 </div>
               </div>
               
               {activeDealInterests.length === 1 ? (
                 // Single deal interest - featured card
                 <div className="space-y-4">
                   {activeDealInterests.map((dealInterest) => (
                     <div key={dealInterest.id} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                       <div className="p-6">
                         <div className="flex items-start justify-between">
                           <div className="flex-1">
                             <div className="flex items-center space-x-3 mb-3">
                               <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                               <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(dealInterest.workflow_state)}`}>
                                 {getStatusDisplayName(dealInterest.workflow_state)}
                               </span>
                             </div>
                                                            <h4 className="text-lg font-semibold text-gray-900 mb-2 leading-tight">
                                 {dealInterest.deal?.headline || `${dealRoomBrandedName} Opportunity`}
                               </h4>
                             <p className="text-sm text-gray-600 mb-4">
                               Review this opportunity and take the next step in your succession journey
                             </p>
                           </div>
                         </div>
                         <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                               <div className="flex items-center space-x-1">
                                 <DocumentTextIcon className="w-4 h-4" />
                                 <span>{dealRoomBrandedName}</span>
                               </div>
                             </div>
                           <a
                             href={`/succession/deals/${dealInterest.deal?.deal_name}/buyer_room`}
                             className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                           >
                             <EyeIcon className="w-4 h-4 mr-2" />
                             View Opportunity
                           </a>
                         </div>
                       </div>
                     </div>
                   ))}
                 </div>
               ) : (
                 // Multiple deal interests - compact cards
                 <div className="space-y-4">
                   <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                     <div className="flex items-center justify-center space-x-2 text-blue-800">
                       <CheckBadgeIcon className="w-5 h-5" />
                       <span className="font-medium">You have {activeDealInterests.length} active opportunities</span>
                     </div>
                   </div>
                   <div className="grid gap-3">
                     {activeDealInterests.slice(0, 2).map((dealInterest) => (
                       <div key={dealInterest.id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 p-4">
                         <div className="flex items-center justify-between">
                           <div className="flex-1">
                             <div className="flex items-center space-x-3 mb-2">
                               <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
                                                                <h4 className="text-sm font-semibold text-gray-900">
                                   {dealInterest.deal?.headline || `${dealRoomBrandedName} Opportunity`}
                                 </h4>
                             </div>
                             <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(dealInterest.workflow_state)}`}>
                               {getStatusDisplayName(dealInterest.workflow_state)}
                             </span>
                           </div>
                           <div className="flex-shrink-0 ml-4">
                             <a
                               href={`/succession/deals/${dealInterest.deal?.deal_name}/buyer_room`}
                               className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                             >
                               View
                               <DocumentTextIcon className="w-4 h-4 ml-1" />
                             </a>
                           </div>
                         </div>
                       </div>
                     ))}
                   </div>
                   {activeDealInterests.length > 2 && (
                     <div className="pt-4 border-t border-gray-200">
                       <a
                         href="/succession/deals"
                         className="block w-full text-center px-4 py-3 bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:from-gray-100 hover:to-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                       >
                         <div className="flex items-center justify-center space-x-2">
                           <span>View All {activeDealInterests.length} Opportunities</span>
                           <PresentationChartBarIcon className="w-4 h-4" />
                         </div>
                       </a>
                     </div>
                   )}
                 </div>
               )}
             </div>
           );
         }

        return null;
      }}
    </Query>
  );
};

// Helper function to capitalize the first letter of a string
const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Helper function to get status badge colors
const getStatusBadgeColor = (status) => {
  const statusColors = {
    'matching': 'bg-gray-100 text-gray-800',
    'invited': 'bg-blue-100 text-blue-800',
    'reviewing_proposal': 'bg-yellow-100 text-yellow-800',
    'shortlisted': 'bg-purple-100 text-purple-800',
    'out_for_receiving_signature': 'bg-orange-100 text-orange-800',
    'awaiting_disclosing_signature': 'bg-orange-100 text-orange-800',
    'negotiating': 'bg-green-100 text-green-800',
    'declined': 'bg-red-100 text-red-800',
    'declined_by_buyer': 'bg-red-100 text-red-800',
    'archived': 'bg-gray-100 text-gray-800'
  };
  return statusColors[status] || 'bg-gray-100 text-gray-800';
};

// Helper function to get user-friendly status display names
const getStatusDisplayName = (status) => {
  const statusNames = {
    'matching': 'Matching',
    'invited': 'Invited',
    'reviewing_proposal': 'Under Review',
    'shortlisted': 'Shortlisted',
    'out_for_receiving_signature': 'NDA Pending',
    'awaiting_disclosing_signature': 'NDA Review',
    'negotiating': 'Negotiating',
    'declined': 'Declined',
    'declined_by_buyer': 'Declined',
    'archived': 'Archived'
  };
  return statusNames[status] || capitalizeFirstLetter(status?.replace('_', ' ') || 'Unknown');
};

const FunnelChart = ({ stages }) => {
  const palette = ['#6678FF', '#5B5BFF', '#7A4DFF', '#A24BFF', '#D24BFF'];
  // Base sizing (already reduced globally); first stage will be 30% taller
  const baseStageHeight = 48;  // default height for stages 2..N
  const gap = 12;              // spacing between stages
  const fullWidth = 400;       // chart width

  const counts = stages.map(s => Number(s.count) || 0);
  const maxCount = Math.max(1, ...counts);

  // Guarantee each stage is visible even if the count is zero
  const minWidth = 72;
  const widths = counts.map(c => (c / maxCount) * (fullWidth - minWidth) + minWidth);

  // Heights per stage, first stage 30% taller
  const stageHeights = stages.map((_, idx) => (idx === 0 ? baseStageHeight * 1.3 : baseStageHeight));
  const totalHeight = stageHeights.reduce((sum, h) => sum + h, 0) + (stages.length - 1) * gap;

  // Helper to build a rounded trapezoid path with per-corner radii
  const trapezoidPath = (tlx, tly, trx, bry, blx, brx, height, radii, topWidth, bottomWidth) => {
    const clamp = (r, w) => Math.max(0, Math.min(r, height / 2, w / 2));
    const rtl = clamp(radii.tl, topWidth);
    const rtr = clamp(radii.tr, topWidth);
    const rbr = clamp(radii.br, bottomWidth);
    const rbl = clamp(radii.bl, bottomWidth);

    const yTop = tly;
    const yBottom = tly + height;

    return [
      `M ${tlx + rtl} ${yTop}`,
      `L ${trx - rtr} ${yTop}`,
      `Q ${trx} ${yTop} ${trx} ${yTop + rtr}`,
      `L ${brx} ${yBottom - rbr}`,
      `Q ${brx} ${yBottom} ${brx - rbr} ${yBottom}`,
      `L ${blx + rbl} ${yBottom}`,
      `Q ${blx} ${yBottom} ${blx} ${yBottom - rbl}`,
      `L ${tlx} ${yTop + rtl}`,
      `Q ${tlx} ${yTop} ${tlx + rtl} ${yTop}`,
      'Z'
    ].join(' ');
  };

  return (
    <div className="w-full flex items-center justify-center">
      <svg viewBox={`0 0 ${fullWidth} ${totalHeight}`} width="100%" height={totalHeight} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Deal flow funnel chart">
        {(() => {
          let yTopCursor = 0;
          return stages.map((stage, idx) => {
            const height = stageHeights[idx];
            const topWidth = widths[idx];
            const isLast = idx === stages.length - 1;
            const nextWidth = !isLast ? widths[idx + 1] : topWidth;
            // Make the final stage narrower at the bottom to mimic a spout
            const tailTaperRatio = 0.6; // 60% of top width for the bottom of the last stage
            const bottomWidthBase = Math.min(nextWidth, topWidth);
            const bottomWidth = isLast ? Math.max(bottomWidthBase * tailTaperRatio, 40) : bottomWidthBase;
            const yTop = yTopCursor;
            const xTopLeft = (fullWidth - topWidth) / 2;
            const xTopRight = xTopLeft + topWidth;
            const xBottomLeft = (fullWidth - bottomWidth) / 2;
            const xBottomRight = xBottomLeft + bottomWidth;
            const fill = palette[idx % palette.length];

            // Top stage: larger round on top corners, subtle on bottom corners
            const stageRadii = idx === 0
              ? { tl: 18, tr: 18, br: 6, bl: 6 }
              : { tl: 10, tr: 10, br: 10, bl: 10 };

            const d = trapezoidPath(
              xTopLeft,
              yTop,
              xTopRight,
              yTop + height,
              xBottomLeft,
              xBottomRight,
              height,
              stageRadii,
              topWidth,
              bottomWidth
            );

            // advance cursor
            yTopCursor += height + gap;

            return (
              <g key={`${stage.label}-${idx}`}>
                <path d={d} fill={fill} opacity="0.92" />
                <text x={fullWidth / 2} y={yTop + height / 2 - 5} textAnchor="middle" fill="#FFFFFF" fontSize="8" fontWeight="500">
                  {stage.label}
                </text>
                <text x={fullWidth / 2} y={yTop + height / 2 + 13} textAnchor="middle" fill="#FFFFFF" fontSize="12" fontWeight="700">
                  {String(stage.count)}
                </text>
              </g>
            );
          });
        })()}
      </svg>
    </div>
  );
};

export default DealRoom; 